import { Bet } from 'types/Bet';
import {
  BasketballLiveEvent,
  LiveEvent,
  LiveEventBet,
  LiveEventOdd,
  TennisLiveEvent,
} from 'types/LiveEvent';
import { MatchOffer } from 'types/MatchOffer';
import { addOrdinalSuffix } from './stringHelper';
import { sortMarketsForDisplay } from './marketCatalog';

export const getFullTimeOdds = (match: any) => {
  const homeWin = match.fullTimeBet?.odds?.filter(
    (x: any) => x.name === '1'
  )[0];
  const draw = match.fullTimeBet?.odds?.filter(
    (x: any) => x.name === 'Draw'
  )[0];
  const awayWin = match.fullTimeBet?.odds?.filter(
    (x: any) => x.name === '2'
  )[0];

  return { homeWin, draw, awayWin };
};

export const getAllBets = (
  match: MatchOffer,
  includeFullTimeBet?: boolean
): Bet[] => {
  // The feed's priority put the markets in a different order for every match;
  // the catalogue fixes the order and leaves markets it does not know behind
  // the ones it does, still in the feed's priority order.
  const bets = sortMarketsForDisplay(match.bets);

  let result = bets;

  if (includeFullTimeBet) {
    result = [match.fullTimeBet, ...bets];
  }

  return result;
};

export const getBetByName = (match: MatchOffer, name: string): Bet => {
  return getAllBets(match, true).filter((x) => x?.name === name)[0];
};

export const getSetWinnerBet = (
  event: TennisLiveEvent
): LiveEventBet | null | undefined => {
  const exactNames = [
    `Set ${event.currentSet} Winner`,
    `Set ${event.currentSet} - Winner`,
  ];

  const exact = event.bets.find((x) => exactNames.includes(x.name));
  if (exact) {
    return exact;
  }

  const currentSetLike = event.bets.find((x) => {
    const name = String(x.name || '').toLowerCase();
    return (
      name.includes(`set ${event.currentSet}`) &&
      name.includes('winner') &&
      !name.includes('game')
    );
  });
  if (currentSetLike) {
    return currentSetLike;
  }

  // Feed can expose only future-set winners (e.g. "Set 2 Winner") while
  // current set winner market is missing. Show first available set-winner.
  return event.bets.find((x) => {
    const name = String(x.name || '').toLowerCase();
    return name.includes('set ') && name.includes('winner');
  });
};

export const getTennisOverUnderBet = (
  event: TennisLiveEvent
): LiveEventBet | null | undefined => {
  const candidates = event.bets.filter(
    (x) => x.name === 'Total Games in Match'
  );

  // Prefer true Over/Under lines so handicap edge (e.g. 8.5, 10.5) is visible.
  for (const bet of candidates) {
    const pair = getTwoWayTotalsOdds(bet, true);
    if (pair) {
      return {
        name: bet.name,
        odds: pair,
      };
    }
  }

  return null;
};

const getTwoWayTotalsOdds = (
  bet: LiveEventBet,
  overUnderOnly: boolean
): LiveEventOdd[] | null => {
  const odds = bet?.odds || [];
  if (!odds.length) {
    return null;
  }

  const token = (odd: LiveEventOdd): string =>
    `${odd.header || ''} ${odd.name || ''} ${odd.name2 || ''} ${odd.name3 || ''}`
      .toLowerCase()
      .trim();

  const over = odds.find((x) => token(x).includes('over'));
  const under = odds.find((x) => token(x).includes('under'));
  if (over && under) {
    return [over, under];
  }

  if (overUnderOnly) {
    return null;
  }

  const odd = odds.find((x) => token(x).includes('odd'));
  const even = odds.find((x) => token(x).includes('even'));
  if (odd && even) {
    return [odd, even];
  }

  return null;
};

export const getNextGoalBet = (
  event: LiveEvent
): LiveEventBet | null | undefined => {
  const totalGoals = event.homeGoals + event.awayGoals;
  const targetNames = [
    `${addOrdinalSuffix(totalGoals + 1)} Goal`,
    'Next Goal',
    'Last Team to Score',
    'First Team to Score',
  ];
  for (const name of targetNames) {
    const bet = event.bets.find((x) => x.name === name);
    if (bet?.odds?.length) {
      return bet;
    }
  }
  return undefined;
};

/** The rest of the match, from bwin (bet365 has no such market). */
export const getRestOfMatchBet = (
  event: LiveEvent
): LiveEventBet | null | undefined =>
  event.bets.find((x) => x.name === 'Rest of Match' && x.odds?.length);

export const getDoubleChanceBet = (
  event: LiveEvent
): LiveEventBet | null | undefined =>
  event.bets.find((x) => x.name === 'Double Chance' && x.odds?.length);

export const getGoalsOverUnderBet = (
  event: LiveEvent
): LiveEventBet | null | undefined => {
  const candidates = event.bets.filter((x) => x.name === 'Match Goals');

  // Soccer over/under column must use Match Goals only.
  for (const bet of candidates) {
    const pair = getTwoWayTotalsOdds(bet, true);
    if (pair) {
      return {
        name: bet.name,
        odds: pair,
      };
    }
  }

  return null;
};

export const getBasketballOverUnderBet = (
  event: BasketballLiveEvent
): LiveEventBet | null | undefined => {
  const bet = event.bets.find((x) => x.name === 'Game Lines');
  if (bet) {
    const overOdd = bet.odds.find((x) => x.header?.startsWith('O '));
    const underOdd = bet.odds.find((x) => x.header?.startsWith('U '));
    if (overOdd && underOdd) {
      return {
        name: 'Game Lines',
        odds: [overOdd, underOdd],
      };
    }
  }
  return null;
};

export const getBasketballHandicapBet = (
  event: BasketballLiveEvent
): LiveEventBet | null | undefined => {
  const bet = event.bets.find((x) => x.name === 'Game Lines');

  if (bet) {
    const homeOdd = bet.odds.find(
      (x) =>
        // The team in NA (old layout) or NA "Spread" with the team in name.
        (x.name === event.home.name ||
          (x.name === 'Spread' && x.name3 === event.home.name)) &&
        x.handicap &&
        (x.handicap.startsWith('+') || x.handicap.startsWith('-'))
    );
    const awayOdd = bet.odds.find(
      (x) =>
        // The team in NA (old layout) or NA "Spread" with the team in name.
        (x.name === event.away.name ||
          (x.name === 'Spread' && x.name3 === event.away.name)) &&
        x.handicap &&
        (x.handicap.startsWith('+') || x.handicap.startsWith('-'))
    );

    if (homeOdd && awayOdd) {
      return {
        name: 'Game Lines',
        odds: [homeOdd, awayOdd],
      };
    }
  }

  return null;
};
